import { ArrowRight, Building2, Package, Receipt, Route, User, Users, Wallet, type LucideIcon } from 'lucide-react'
import { SearchCategory, SearchResultItem as SearchResultItemType } from '../types/types'

const CATEGORY_ICON: Record<SearchCategory, LucideIcon> = {
    [SearchCategory.Loads]: Package,
    [SearchCategory.Invoices]: Receipt,
    [SearchCategory.Trucks]: Route,
    [SearchCategory.Drivers]: User,
    [SearchCategory.Contractors]: Users,
    [SearchCategory.Payroll]: Wallet,
    [SearchCategory.Brokers]: Building2,
    [SearchCategory.Trips]: Route,
}

function highlightMatch(text: string, query: string): React.ReactNode {
    if (!query || query.length < 2) return text

    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'))

    return parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="rounded-sm bg-yellow-200 px-0.5 text-yellow-900 dark:bg-amber-400/30 dark:text-amber-200">
                {part}
            </mark>
        ) : (
            part
        )
    )
}

interface Props {
    item: SearchResultItemType
    isActive: boolean
    query: string
    onClick: () => void
    onMouseEnter: () => void
}

export function SearchResultRow({ item, isActive, query, onClick, onMouseEnter }: Props) {
    const Icon = CATEGORY_ICON[item.category]

    return (
        <div
            className={`flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2.5 transition-colors ${isActive ? 'bg-default' : 'hover:bg-default'}`}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            role="option"
            aria-selected={isActive}
        >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-secondary text-foreground">
                <Icon size={17} />
            </span>
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{highlightMatch(item.title, query)}</div>
                {item.subtitle && <div className="truncate text-xs text-foreground-muted">{highlightMatch(item.subtitle, query)}</div>}
            </div>
            {item.status && (
                <span className="shrink-0 rounded-md bg-surface-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">
                    {item.status}
                </span>
            )}
            <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-border bg-surface transition-opacity ${isActive ? 'opacity-100' : 'opacity-0'}`}
            >
                <ArrowRight size={14} className="text-foreground-muted" />
            </span>
        </div>
    )
}
